import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector, useAppStore } from './redux/hooks'
import { Outlet, useNavigate, useParams } from 'react-router'
import { canViewAdhocEncounters, RootRouteContentType, SyncResultStatus, WorkItemAction } from '@fusion-advantage/formlib'
import { toggleTrainingMode } from './redux/appSlice'
import { dashboardDataReloadFromLdb } from './redux/dashboardDataSlice'
import { adhocViewEncounter } from './redux/syncthunk/adhocViewEncounterThunk'
import { appSync } from './redux/syncthunk/syncThunk'
import { SyncType } from './redux/syncthunk/SyncConfig'
import { LxAppIntro } from './stories/content/dashboard/LxAppIntro'

export const EncounterRouteParams = {
	encounterGuid: 'encounterGuid',
} as const

const GUID_PATTERN = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/

enum EncounterStatus {
	Loading = 'loading',
	NeedsLogin = 'needsLogin',
	TrainingMode = 'trainingMode',
	NotAuthorized = 'notAuthorized',
	Failed = 'failed',
	Ready = 'ready',
}

// opens a real encounter straight from a link, skipping the dashboard;
// the session is the normal web cookie; like Manage Encounters it requires
// the Auditor or ViewAdhocEncounters role, and who may edit is decided by
// linkedEncounterWriteMode
export const EncounterRoute = React.memo(props => {
	const booted = useAppSelector(state => state.app.booted)
	const content = useAppSelector(state => state.app.content)
	const isTrainingMode = useAppSelector(state => state.app.isTrainingMode)
	const dispatch = useAppDispatch()
	const appStore = useAppStore()
	const navigate = useNavigate()
	const routeParams = useParams()
	const encounterGuid = routeParams[EncounterRouteParams.encounterGuid]?.toUpperCase()
	const isValidGuid = Boolean(encounterGuid && GUID_PATTERN.test(encounterGuid))
	const [status, setStatus] = useState(EncounterStatus.Loading)

	useEffect(() => {
		(async function() {
			if(!encounterGuid || !isValidGuid || !booted) {
				return
			}
			// Training mode impersonates the Test Mode user, for whom the
			// export returns a fake encounter instead of this one
			if(isTrainingMode) {
				setStatus(EncounterStatus.TrainingMode)
				return
			}
			setStatus(EncounterStatus.Loading)
			// the normal startup sync first, so the user's roles are known
			// before anything about this encounter is downloaded
			const rSync = await dispatch(appSync({
				type: SyncType.Boot,
			}))
			if(rSync.isErr()) {
				// no session cookie: the login screen returns here afterward
				setStatus(rSync.error.status === SyncResultStatus.NoAuth
					? EncounterStatus.NeedsLogin
					: EncounterStatus.Failed)
				return
			}
			await dispatch(dashboardDataReloadFromLdb())
			// same security as the Manage Encounters button (the real login,
			// not a Training mode identity)
			if(!canViewAdhocEncounters(appStore.getState().dashboardData.authedIdentity)) {
				setStatus(EncounterStatus.NotAuthorized)
				return
			}
			// then open it the way Manage Encounters' View does
			const rView = await dispatch(adhocViewEncounter({
				syncType: SyncType.Dashboard,
				encounterGuid,
				action: WorkItemAction.ContinueForm,
				openedFromLink: true,
			}))
			if(rView.isErr()) {
				setStatus(rView.error.status === SyncResultStatus.NoAuth
					? EncounterStatus.NeedsLogin
					: EncounterStatus.Failed)
				return
			}
			setStatus(EncounterStatus.Ready)
		})()
	}, [encounterGuid, isValidGuid, booted, isTrainingMode])

	// once the encounter is closed, carry on in the normal app at the root
	// (so a reload doesn't reopen the encounter)
	useEffect(() => {
		if(status === EncounterStatus.Ready && content.type === RootRouteContentType.Dashboard) {
			navigate('/', {replace: true})
		}
	}, [status, content.type])

	if(!encounterGuid || !isValidGuid) {
		return <h2>Encounter URL does not contain a valid encounter GUID</h2>
	}

	switch(status) {
		case EncounterStatus.NeedsLogin:
			return <LxAppIntro />
		case EncounterStatus.TrainingMode:
			return <>
				<h2>Training mode is on</h2>
				<p>A real encounter can't be opened in Training mode.</p>
				<button onClick={() => dispatch(toggleTrainingMode())}>Turn off Training mode and open the encounter</button>
			</>
		case EncounterStatus.NotAuthorized:
			return <>
				<h2>Not authorized to open encounters from a link</h2>
				<p>This requires the same access as Manage Encounters (the Auditor or ViewAdhocEncounters role).</p>
			</>
		case EncounterStatus.Failed:
			return <h2>The encounter could not be opened; see the console for details</h2>
	}

	if(content.type !== RootRouteContentType.Form) {
		return <h2>Loading encounter {encounterGuid}...</h2>
	}

	return <Outlet />
})
